import DocumentPage from './DocumentPage';

export const inventoryDocument = {
  slug: 'inventory',
  route: '/inventory',
  title: 'Inventory',
  category: 'Finance & Procurement',
  format: 'DOCX',
  description:
    'The Inventory module manages stock operations, product quantities, warehouse movement, reporting, and warehouse configuration in real time.',
  metrics: [
    { label: 'Primary focus', value: 'Stock control' },
    { label: 'Core records', value: 'Transfers and products' },
    { label: 'Planning', value: 'Replenishment and valuation' },
  ],
  workflow: [
    'Process receipts, deliveries, inventory adjustments, and scrap entries from the operations area.',
    'Monitor replenishment suggestions and run the scheduler when stock needs replenishment control.',
    'Review product, stock, and valuation reports, then maintain warehouse and product configuration.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The Inventory Module in Odoo 17 is used to manage and track all stock-related operations within an organization. It helps in controlling product quantities, tracking stock movement, managing warehouses, and ensuring accurate inventory levels.',
        'This module allows users to perform operations like receiving products, delivering products, transferring stock between locations, and monitoring inventory in real time.',
      ],
    },
    {
      id: 'operations',
      title: 'Operations',
      body: [
        'The Operations area is used to manage all movement-related stock activities inside the warehouse.',
        'It includes receipts, deliveries, physical inventory adjustments, and scrap operations for damaged or unusable products.',
      ],
      subsections: [
        {
          id: 'receipts',
          title: 'Receipts',
          body: [
            'Go to Operations -> Transfers -> Receipts.',
            'The Receipts card represents incoming shipments from vendors or suppliers. It shows the number of receipts that need to be processed.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image.png',
            alt: 'Inventory receipt transfer form for incoming products',
            caption: 'Receipts are used to record incoming stock from vendors into the warehouse.',
          },
        },
        {
          id: 'deliveries',
          title: 'Deliveries',
          body: [
            'Go to Operations -> Transfers -> Deliveries.',
            'The Deliveries option is used when products are sent from the warehouse to customers.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image2.png',
            alt: 'Inventory delivery transfer form for outgoing shipment',
            caption: 'Deliveries track outgoing products and customer dispatch operations.',
          },
        },
        {
          id: 'physical-inventory',
          title: 'Physical Inventory',
          body: [
            'Go to Operations -> Transfers -> Physical Inventory.',
            'The Physical Inventory option is used to adjust stock quantities when the actual physical stock does not match the quantity recorded in the system.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image3.png',
            alt: 'Inventory adjustment list showing on hand and counted quantity',
            caption: 'Physical inventory adjustments help correct system stock based on real warehouse counts.',
          },
        },
        {
          id: 'scrap',
          title: 'Scrap',
          body: [
            'Go to Operations -> Transfers -> Scrap.',
            'The Scrap option is used to remove damaged or unusable products from the inventory.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image4.png',
            alt: 'Scrap order form removing unusable inventory',
            caption: 'Scrap orders remove damaged stock while keeping the inventory record accurate.',
          },
        },
      ],
    },
    {
      id: 'procurement-controls',
      title: 'Procurement Controls',
      body: [
        'Inventory also supports planning tools for keeping stock available when demand changes.',
        'These controls help maintain minimum stock levels and trigger procurement logic when quantities become low.',
      ],
      subsections: [
        {
          id: 'replenishment',
          title: 'Replenishment',
          body: [
            'Go to Operations -> Procurement -> Replenishment.',
            'The Replenishment option helps maintain minimum stock levels. When product quantity falls below a certain limit, the system suggests creating a purchase order to restock the product.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image5.png',
            alt: 'Inventory replenishment screen showing product to reorder',
            caption: 'Replenishment suggests how much stock should be reordered when forecast levels are low.',
          },
        },
        {
          id: 'run-scheduler',
          title: 'Run Scheduler',
          body: [
            'Go to Operations -> Run Scheduler.',
            'The Run Scheduler option automatically checks stock levels and triggers procurement rules such as reordering products when the quantity becomes low.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image6.png',
            alt: 'Run Scheduler confirmation dialog in inventory',
            caption: 'Run Scheduler applies reordering logic and reserves stock for waiting operations.',
          },
        },
      ],
    },
    {
      id: 'products-and-reporting',
      title: 'Products and Reporting',
      body: [
        'The Products menu is used to manage product information in the inventory system. Users can create, update, and manage products stored in the warehouse.',
        'Product information includes product name, product category, product type, cost and sale price, and stock quantity. Proper product management ensures accurate inventory tracking.',
        'The reporting area shows the current state of stock, storage locations, movement history, and total stock value.',
      ],
      subsections: [
        {
          id: 'products',
          title: 'Products',
          body: [
            'Go to Product -> Products.',
            'Use this menu to maintain complete product data used throughout the warehouse workflow.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image7.png',
            alt: 'Inventory product form with quantity and pricing information',
            caption: 'The product form stores stockable product details used for tracking and replenishment.',
          },
          bullets: [
            'Product name',
            'Product category',
            'Product type',
            'Cost and sale price',
            'Stock quantity',
          ],
        },
        {
          id: 'stock-report',
          title: 'Stock Report',
          body: [
            'Go to Reporting -> Stock.',
            'The Stock report shows the current quantity of products available in the warehouse.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image8.png',
            alt: 'Stock report showing available inventory and quantities',
            caption: 'The stock report helps monitor available stock, low stock products, and current inventory levels.',
          },
          bullets: [
            'Monitor available stock.',
            'Identify low stock products.',
            'Track inventory levels.',
          ],
        },
        {
          id: 'locations-report',
          title: 'Locations Report',
          body: [
            'Go to Reporting -> Locations.',
            'The locations report shows where products are stored in the warehouse.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/image9.png',
            alt: 'Locations report showing internal warehouse stock locations',
            caption: 'Locations reporting shows stock placement and reserved quantity by storage location.',
          },
        },
        {
          id: 'moves-history',
          title: 'Moves History',
          body: [
            'Go to Reporting -> Moves History.',
            'Moves history shows all product movements in the inventory.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/imagea.png',
            alt: 'Moves History report showing stock movements and statuses',
            caption: 'Moves History records inbound, outbound, adjustment, and scrap movements for audit tracking.',
          },
        },
        {
          id: 'valuation',
          title: 'Valuation',
          body: [
            'Go to Reporting -> Valuation.',
            'Inventory valuation shows the total value of stock.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/imageb.png',
            alt: 'Stock valuation report showing inventory value totals',
            caption: 'Valuation reporting helps users understand the financial value of current stock.',
          },
        },
      ],
    },
    {
      id: 'configuration',
      title: 'Configuration',
      body: [
        'The Configuration menu defines the warehouse structure and product control records used by inventory workflows.',
      ],
      subsections: [
        {
          id: 'warehouses',
          title: 'Warehouses',
          body: [
            'Go to Configuration -> Warehouse Management -> Warehouses.',
            'Warehouses are locations where products are stored.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/imagec.png',
            alt: 'Warehouse list for inventory storage locations',
            caption: 'Warehouse records define the physical storage points used by the business.',
          },
        },
        {
          id: 'operation-types',
          title: 'Operation Types',
          body: [
            'Go to Configuration -> Warehouse Management -> Operation Types.',
            'These define the specific flow of goods in or out of your warehouse.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/imaged.png',
            alt: 'Operation Types list showing receipt and delivery flows',
            caption: 'Operation Types control whether stock is moving in, out, or through internal warehouse routes.',
          },
        },
        {
          id: 'product-categories',
          title: 'Product Categories',
          body: [
            'Go to Products -> Product Categories.',
            'These are groups used to organize your products for reporting and accounting purposes.',
          ],
          figure: {
            src: '/assets/doc-images/inventory/imagee.png',
            alt: 'Product Categories list used to group inventory items',
            caption: 'Product categories support cleaner organization, reporting, and accounting structure.',
          },
        },
        {
          id: 'barcode-nomenclatures',
          title: 'Barcode Nomenclatures',
          body: [
            'Go to Products -> Barcode Nomenclatures.',
            'Barcode nomenclatures define barcode formats used for scanning products.',
          ],
        },
      ],
    },
  ],
  related: ['purchase', 'invoice', 'fleet'],
};

export default function InventoryPage() {
  return <DocumentPage document={inventoryDocument} />;
}
